import { useEffect, useRef } from 'react';
import { BUILD_ID } from '../buildInfo';

// Polls the static build/version.json (regenerated on every deploy — see
// scripts/write-version-file.js) and calls onUpdateAvailable once the first
// time it sees a buildId different from the one this bundle was built with.
// This is how the app notices a new deploy while it's already open — the
// mobile installed-web-app shell has no pull-to-refresh to fall back on, and
// nobody remembers to bump a version number by hand this way.
//
// cache: 'no-store' plus a cache-busting query param defeat any HTTP or CDN
// caching of the static file; a plain browser reload wouldn't be enough on
// its own to guarantee a fresh fetch.
function useAppUpdateCheck(onUpdateAvailable, intervalMs = 5 * 60 * 1000) {
    const notifiedRef = useRef(false);
    const callbackRef = useRef(onUpdateAvailable);
    callbackRef.current = onUpdateAvailable;

    useEffect(() => {
        let cancelled = false;

        const checkVersion = async () => {
            if (notifiedRef.current) return;
            try {
                const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
                const data = await response.json();
                if (cancelled || notifiedRef.current) return;

                if (data && data.buildId && data.buildId !== BUILD_ID) {
                    notifiedRef.current = true;
                    callbackRef.current();
                }
            } catch (error) {
                // Ignore transient network errors; the next interval (or the next
                // time the tab is foregrounded) will retry.
            }
        };

        checkVersion();
        const intervalId = setInterval(checkVersion, intervalMs);

        // Also check right away whenever the installed app is brought back to
        // the foreground — the interval alone might not fire for a while if
        // the app was backgrounded/suspended (common for a mobile PWA shell).
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') checkVersion();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [intervalMs]);
}

export default useAppUpdateCheck;
